import { Stack, Typography, Button, Box, LinearProgress } from "@mui/material";
import {
  useCreateQuestionOptionMutation,
  useGetQuestionOptionsQuery,
} from "src/api/services/questionOptionsApi";
import DataTable from "src/components/DataTable";
import FormInput from "src/components/FormInput";
import DrawerForm from "src/shared/DrawerForm";
import { useFromAndTo } from "src/hooks/useFromAndTo";
import { FormEvent, useCallback, useMemo, useState } from "react";
import { useManageDrawer } from "src/hooks/useManageDrawer";
import { usePagination } from "src/hooks/usePagination";
import {
  Column,
  InputType,
  QuestionOption,
  QuestionOptionInsert,
} from "src/api/types";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import Filter from "src/components/TableFilter";
import { filters } from "./utils/filters";
import { useSearchParams } from "react-router-dom";
import { LABEL } from "src/constants/searchParams";

const useColumns = (): Column<Omit<QuestionOption, "position">>[] => {
  return [
    {
      id: "slug",
      label: "Slug",
      orderable: false,
      align: "center",
      format: (row) => row.slug,
      render: (row) => <Typography>{row.slug}</Typography>,
    },
    {
      id: "label",
      label: "Label",
      orderable: false,
      align: "center",
      format: (row) => row.label,
      render: (row) => <Typography>{row.label}</Typography>,
    },
    {
      id: "value",
      label: "Value",
      orderable: false,
      align: "center",
      format: (row) => row.value,
      render: (row) => <Typography>{row.value}</Typography>,
    },
  ];
};

const defaultValues: QuestionOptionInsert = {
  slug: "",
  label: "",
  value: "",
};

const useInputs = (defaultValues: QuestionOptionInsert) => {
  const [values, setValues] = useState<QuestionOptionInsert>(defaultValues);

  const setFields = useCallback(
    <K extends keyof QuestionOptionInsert>(
      key: K,
      value: QuestionOptionInsert[K],
    ) => {
      setValues((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const inputs: InputType<QuestionOptionInsert>[] = useMemo(
    () => [
      {
        type: "text",
        name: "slug",
        label: "Slug",
        disabled: true,
        required: false,
        value: values.slug ?? "",
        onChange: (v) => setFields("slug", v ?? ""),
      },
      {
        type: "text",
        name: "value",
        label: "Value",
        required: true,
        value: values.value ?? "",
        onChange: (v) => setFields("value", v ?? ""),
      },
      {
        type: "text",
        name: "label",
        label: "Label",
        required: false,
        value: values.label ?? "",
        onChange: (v) => setFields("label", v ?? ""),
      },
    ],
    [setFields, values],
  );

  return { values, inputs, setValues };
};

const QuestionOptions = () => {
  const [open, { handleOpen, handleClose }] = useManageDrawer();
  const { from, to } = useFromAndTo();
  const pagination = usePagination();
  const columns = useColumns();
  const { inputs, values, setValues } = useInputs(defaultValues);
  const logErr = useSentryLogger("error");
  const [createQuestionOption, { isLoading }] =
    useCreateQuestionOptionMutation();
  const [searchParams] = useSearchParams();

  const queryParams = useMemo(
    () => ({
      from,
      to,
      label: searchParams.get(LABEL) ?? undefined,
    }),
    [from, searchParams, to],
  );

  const { data, isFetching } = useGetQuestionOptionsQuery(queryParams);

  const { options, total } = useMemo(() => {
    if (data == null) return { options: [], total: 0 };

    return { options: data.data, total: data.count };
  }, [data]);

  const height = useMemo(() => window.innerHeight - 104 - 52 - 56, []);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      try {
        await createQuestionOption(values).unwrap();
        handleClose();
        setValues(defaultValues);
      } catch (error) {
        logErr("Failed to create question", error);
      }
    },
    [createQuestionOption, handleClose, logErr, setValues, values],
  );
  return (
    <>
      <Stack gap={2} sx={{ width: "100%", minWidth: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h5">Question Options</Typography>
          <Button onClick={handleOpen}>+ Add Question</Button>
        </Stack>

        <Stack direction="row" gap="16px" flexWrap="wrap">
          {filters.map((filter) => (
            <Filter key={filter.name} current={filter} />
          ))}
        </Stack>

        <Stack gap={0} sx={{ width: "100%", minWidth: 0 }}>
          <Box height={4}>{isFetching ? <LinearProgress /> : null}</Box>
          <DataTable
            data={options}
            columns={columns}
            height={height}
            primaryKey="id"
            pagination={pagination}
            total={total}
          />
        </Stack>
      </Stack>
      <DrawerForm
        open={open}
        loading={isLoading}
        label="Add Question Option"
        handleSubmit={handleSubmit}
        onClose={handleClose}
      >
        <Stack gap={3}>
          {inputs.map((input) => (
            <FormInput key={input.label} current={input} />
          ))}
        </Stack>
      </DrawerForm>
    </>
  );
};

export default QuestionOptions;
